import React, { useMemo, useState } from 'react';
import { Search, FileText, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTemplates } from '@/hooks/useTemplates';
import { useAuthStore } from '@/stores/useAuthStore';
import { useLocalizationStore } from '@/stores/useLocalizationStore';
import type { Template, TemplateSpecialty } from '@/services/api/templatesApi';
import { readRecentTemplateIds } from './templateRecents';

interface TemplatePickerPhaseProps {
  onPick: (template: Template) => void;
  onSkip: () => void;
}

export const TemplatePickerPhase: React.FC<TemplatePickerPhaseProps> = ({ onPick, onSkip }) => {
  const [query, setQuery] = useState('');
  const { user } = useAuthStore();
  const { config: { language } } = useLocalizationStore();

  const { data: templatesData, isLoading } = useTemplates({
    search: query || undefined,
    limit: 50,
    isActive: true,
  });

  // Auth store may have a specialty on the user; fall back to 'general'.
  // Cast tolerated since the field isn't typed yet (recon flagged it as a gap).
  const userSpecialty = ((user as unknown as { specialty?: TemplateSpecialty })?.specialty
    || 'general') as TemplateSpecialty;

  const templates: Template[] = useMemo(
    () => (templatesData?.data?.data as Template[] | undefined) || [],
    [templatesData],
  );

  const { recent, specialty, others } = useMemo(() => {
    const recentIds = new Set(readRecentTemplateIds());
    const recentList: Template[] = [];
    const specialtyList: Template[] = [];
    const otherList: Template[] = [];

    for (const t of templates) {
      if (recentIds.has(t.id)) {
        recentList.push(t);
      } else if (t.specialty === userSpecialty) {
        specialtyList.push(t);
      } else {
        otherList.push(t);
      }
    }

    // Preserve recent order from storage
    const order = readRecentTemplateIds();
    recentList.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));

    return { recent: recentList, specialty: specialtyList, others: otherList };
  }, [templates, userSpecialty]);

  const renderName = (t: Template) =>
    (language === 'ar' && t.name?.ar) ? t.name.ar : (t.name?.en || t.id);

  const renderDesc = (t: Template) => {
    const d = t.description;
    if (!d) return null;
    return (language === 'ar' && d.ar) ? d.ar : d.en;
  };

  const TemplateRow: React.FC<{ template: Template }> = ({ template }) => (
    <button
      onClick={() => onPick(template)}
      className="w-full text-start p-3 rounded-lg flex items-start gap-3 transition-colors hover:bg-[var(--color-bg-secondary)]"
    >
      <div
        className="p-2 rounded-lg flex-shrink-0"
        style={{ backgroundColor: 'var(--color-primary-100)' }}
      >
        <FileText className="w-4 h-4" style={{ color: 'var(--color-primary-600)' }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
          {renderName(template)}
        </p>
        {renderDesc(template) && (
          <p className="text-xs truncate mt-0.5" style={{ color: 'var(--color-text-tertiary)' }}>
            {renderDesc(template)}
          </p>
        )}
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
          {template.category?.replace(/-/g, ' ')} · {template.specialty}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 flex-shrink-0 mt-2" style={{ color: 'var(--color-text-tertiary)' }} />
    </button>
  );

  const Section: React.FC<{ title: string; items: Template[] }> = ({ title, items }) => {
    if (items.length === 0) return null;
    return (
      <div className="space-y-1">
        <p
          className="text-xs font-semibold uppercase tracking-wide px-3 py-2"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          {title}
        </p>
        {items.map((t) => <TemplateRow key={t.id} template={t} />)}
      </div>
    );
  };

  const isRtl = language === 'ar';
  const totalShown = recent.length + specialty.length + others.length;

  return (
    <div className="flex flex-col h-full" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="px-6 pt-6 pb-3">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          {isRtl ? 'اختر قالب الزيارة' : 'Choose a template for this visit'}
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
          {isRtl
            ? 'يحدد القالب كيفية هيكلة المخرجات بعد التسجيل.'
            : 'The template shapes how the recording is structured into a note.'}
        </p>
      </div>

      <div className="px-6 pb-3">
        <div className="relative">
          <Search
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4"
            style={{
              color: 'var(--color-text-tertiary)',
              [isRtl ? 'right' : 'left']: 12,
            }}
          />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isRtl ? 'بحث في القوالب...' : 'Search templates...'}
            className="w-full py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border-primary)',
              paddingLeft: isRtl ? 12 : 36,
              paddingRight: isRtl ? 36 : 12,
            }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {isLoading && (
          <div className="text-center py-12 text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {isRtl ? 'جارٍ التحميل...' : 'Loading templates...'}
          </div>
        )}

        {!isLoading && totalShown === 0 && (
          <div className="text-center py-12">
            <FileText className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {isRtl ? 'لا توجد قوالب مطابقة' : 'No matching templates'}
            </p>
          </div>
        )}

        <Section title={isRtl ? 'الأخيرة' : 'Recent'} items={recent} />
        <Section
          title={isRtl ? `تخصصك (${userSpecialty})` : `Your specialty (${userSpecialty})`}
          items={specialty}
        />
        <Section title={isRtl ? 'كل القوالب' : 'All templates'} items={others} />
      </div>

      <div
        className="px-6 py-3 border-t flex items-center justify-between"
        style={{ borderColor: 'var(--color-border-primary)' }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
        >
          <Sparkles />
          {isRtl ? 'تخطي — استخدم SOAP عام' : 'Skip — use generic SOAP'}
        </Button>
      </div>
    </div>
  );
};
